import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Box, Typography, Button, Snackbar, Alert, CircularProgress, Paper, Grid, Avatar, Chip, Tooltip, LinearProgress, IconButton, Input, FormControlLabel, Switch, TextField, Stack, Tabs, Tab, Divider, useMediaQuery, useTheme, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { apiFetch } from '../utils/apiFetch';
const AnalyticsDashboard = lazy(() => import('../components/AnalyticsDashboard.jsx'));
import DarkModeToggle from '../components/DarkModeToggle.jsx';
import EnhancedErrorBoundary from '../components/EnhancedErrorBoundary.jsx';
import MobileBottomNavigation from '../components/MobileBottomNavigation.jsx';
import useRealTimeUpdates from '../hooks/useRealTimeUpdates.js';
import { DashboardSkeleton, StatCardSkeleton, ChartSkeleton } from '../components/SkeletonLoaders.jsx';
import { useMoverAuth, useMoverBookings, useMoverReviews } from '../hooks/useApiWithRetry.js';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { SketchPicker } from 'react-color';
import { useDropzone } from 'react-dropzone';
import KeyIcon from '@mui/icons-material/Key';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import PieChartIcon from '@mui/icons-material/PieChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import LineStyleIcon from '@mui/icons-material/LineStyle';
import { PieChart, BarChart, LineChart } from '@mui/x-charts';
import AdvancedBookingManagement from '../components/AdvancedBookingManagement';
import CustomReportingTools from '../components/CustomReportingTools';

const MoverDashboard = ({ token, onProfileUpdate }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Real-time updates hook
  const {
    bookings: realtimeBookings,
    setBookings: setRealtimeBookings,
    notifications,
  } = useRealTimeUpdates(token);
  
  const [profile, setProfile] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [availability, setAvailability] = useState('');
  const [availLoading, setAvailLoading] = useState(false);
  const [recentBookings, setRecentBookings] = useState([]);
  const [upcomingMoves, setUpcomingMoves] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [growth, setGrowth] = useState('+0%');
  const [performance, setPerformance] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [bookingsPerPage, setBookingsPerPage] = useState(5);
  const [errors, setErrors] = useState({});
  const [dashboardTab, setDashboardTab] = useState(0);

  // --- Company Logo Upload and Brand Color Picker ---
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [brandColor, setBrandColor] = useState(profile?.brandColor || '#1976d2');
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Drag-and-drop for logo
  const onDropLogo = React.useCallback(acceptedFiles => {
    if (acceptedFiles.length) {
      setLogoFile(acceptedFiles[0]);
      setLogoPreview(URL.createObjectURL(acceptedFiles[0]));
    }
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: onDropLogo, accept: { 'image/*': [] }, maxFiles: 1 });

  // Fetch profile (with token in real app)
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        // Use fetch with credentials for cookie-based auth
  const headers = token && token !== 'cookie' ? { Authorization: `Bearer ${token}` } : {};
  const res = await apiFetch('/api/movers/me', { credentials: 'include', headers });
        const data = await res.json();
        const mover = data?.moverCompany || data?.mover; // support both shapes
        if (!res.ok || !mover) {
          setSnackbar({ open: true, message: data?.error || 'Not authenticated. Please log in again.', severity: 'error' });
          setProfile(null);
          setForm({});
          setAvailability('');
          setLoading(false);
          return;
        }
        setProfile(mover);
        setForm(mover);
        setAvailability(mover.availability ? mover.availability.join(', ') : '');
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to load profile.', severity: 'error' });
        console.error('Error loading profile:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  useEffect(() => {
    // Fetch recent bookings and upcoming moves
    const fetchBookings = async () => {
      try {
        const res = await apiFetch('/api/bookings?mover=me');
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        const sorted = data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        // Only include new/pending/confirmed jobs in recentBookings
        const filteredBookings = sorted.filter(b => b.status === 'pending' || b.status === 'confirmed').slice(0, bookingsPerPage);
        setRecentBookings(filteredBookings);
        setRealtimeBookings(filteredBookings); // Initialize real-time bookings
        setUpcomingMoves(sorted.filter(b => new Date(b.moveDate) >= new Date()).slice(0, bookingsPerPage));
      } catch {}
    };
    // Fetch latest reviews
    const fetchReviews = async () => {
      try {
        const res = await apiFetch('/api/reviews/me');
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        const reviewsData = data.reviews || data || [];
        setReviews(Array.isArray(reviewsData) ? reviewsData.slice(-3).reverse() : []);
      } catch {}
    };
    if (profile?._id) fetchBookings();
    if (token) fetchReviews();
  }, [profile, token, bookingsPerPage, setRealtimeBookings]);

  useEffect(() => {
    if (!profile?._id) return;
    apiFetch('/api/analytics/performance')
      .then(async res => {
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        setPerformance(data);
      })
      .catch(() => setPerformance(null));
  }, [profile, token]);

  // Sync real-time bookings with recent bookings
  useEffect(() => {
    if (realtimeBookings.length > 0) {
      setRecentBookings(realtimeBookings.filter(b => b.status === 'pending' || b.status === 'confirmed'));
    }
  }, [realtimeBookings]);

  // --- All Bookings for Analytics ---
  const [allBookings, setAllBookings] = useState([]);
  useEffect(() => {
    if (profile?._id) {
      apiFetch('/api/bookings?mover=me&all=true')
        .then(async res => {
          const data = await res.json();
          if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
          setAllBookings(data || []);
        })
        .catch(() => setAllBookings([]));
    }
  }, [profile]);

  // Calculate growth (jobs completed this month vs last month)
  useEffect(() => {
    const fetchGrowth = async () => {
      if (!profile?._id) return;
      try {
        const res = await apiFetch('/api/analytics/growth');
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
        setGrowth(data.growth || '+0%');
      } catch {
        setGrowth('+0%');
      }
    };
    fetchGrowth();
  }, [profile, token]);

  // Profile completion calculation
  const requiredFields = ['name', 'email', 'locations', 'services'];
  const profileCompletion = Math.round(
    requiredFields.filter(f => form[f] && (Array.isArray(form[f]) ? form[f].length : String(form[f]).trim())).length / requiredFields.length * 100
  );

  // Validation helpers
  const validateEmail = email => /.+@.+\..+/.test(email);
  const validatePhone = phone => /^\+?[0-9\-\s]{7,}$/.test(phone);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEdit = () => setEdit(true);
  const handleCancel = () => { setEdit(false); setForm(profile); setErrors({}); };

  const handleFieldChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
    // Live validation
    if (name === 'email') setErrors(errs => ({ ...errs, email: validateEmail(value) ? '' : 'Invalid email' }));
    if (name === 'phone') setErrors(errs => ({ ...errs, phone: validatePhone(value) ? '' : 'Invalid phone' }));
  };

  const handleSave = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiFetch(`/api/movers/${profile._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          brandColor,
          vacationMode,
          services: typeof form.services === 'string' ? form.services.split(',').map(s => s.trim()) : form.services,
          locations: typeof form.locations === 'string' ? form.locations.split(',').map(l => l.trim()) : form.locations,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      setSnackbar({ open: true, message: 'Profile updated!', severity: 'success' });
      setEdit(false);
      setProfile({ ...profile, ...form });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update profile.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Availability management
  const handleAvailabilitySave = async () => {
    setAvailLoading(true);
    try {
      const availArr = availability.split(',').map(d => d.trim()).filter(Boolean);
      const res = await apiFetch(`/api/movers/${profile._id}/availability`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ availability: availArr }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      setSnackbar({ open: true, message: 'Availability updated!', severity: 'success' });
      setProfile(p => ({ ...p, availability: availArr }));
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update availability.', severity: 'error' });
    } finally {
      setAvailLoading(false);
    }
  };

  // Profile photo upload handler
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('profileImage', file);
    try {
      const uploadRes = await apiFetch('/api/movers/me/profile-image', {
        method: 'POST',
        body: formData,
      });
      if (!uploadRes.ok) {
        const data = await uploadRes.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${uploadRes.status}`);
      }
      // Refetch profile to ensure avatar updates everywhere
      if (onProfileUpdate) {
        await onProfileUpdate();
      }
      const profileRes = await apiFetch('/api/movers/me');
      const profileData = await profileRes.json();
      if (!profileRes.ok) throw new Error(profileData?.error || `HTTP ${profileRes.status}`);
      setProfile(profileData.mover);
      setSnackbar({ open: true, message: 'Profile photo updated!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to upload photo.', severity: 'error' });
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Company logo upload handler
  const handleLogoUpload = async () => {
    if (!logoFile) return;
    const formData = new FormData();
    formData.append('companyLogo', logoFile);
    try {
      const uploadRes = await apiFetch('/api/movers/me/company-logo', {
        method: 'POST',
        body: formData,
      });
      if (!uploadRes.ok) {
        const data = await uploadRes.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${uploadRes.status}`);
      }
      setSnackbar({ open: true, message: 'Logo uploaded!', severity: 'success' });
      setLogoFile(null);
      setLogoPreview('');
      // Optionally refetch profile
    } catch {
      setSnackbar({ open: true, message: 'Failed to upload logo.', severity: 'error' });
    }
  };

  const handleColorChange = color => {
    setBrandColor(color.hex);
    setForm(f => ({ ...f, brandColor: color.hex }));
  };

  // Export bookings to CSV
  const handleExportBookings = async () => {
    setDownloading(true);
    try {
      const response = await apiFetch('/api/analytics/export/bookings');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      // Create download link
      const url = window.URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bookings-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSnackbar({ open: true, message: 'Bookings exported successfully!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to export bookings.', severity: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  // --- Service Descriptions ---
  const [serviceDescriptions, setServiceDescriptions] = useState(form.serviceDescriptions || {});
  const handleServiceDescChange = (svc, desc) => {
    setServiceDescriptions(sd => ({ ...sd, [svc]: desc }));
    setForm(f => ({ ...f, serviceDescriptions: { ...serviceDescriptions, [svc]: desc } }));
  };

  // --- Pricing Fields (for accurate quoting) ---
  // These fields are for admin/owner only, not shown to customers directly
  const [pricing, setPricing] = useState(form.pricing || {
    baseRate: '',
    hourlyRate: '',
    minHours: '',
    travelFee: '',
    fuelSurcharge: '',
    stairFee: '',
    longCarryFee: '',
    oversizedItemFee: '',
    afterHoursFee: '',
    weekendSurcharge: '',
    insuranceFee: '',
    depositRequired: '',
    cancellationFee: '',
    additionalNotes: '',
  });

  const handlePricingChange = e => {
    const { name, value } = e.target;
    setPricing(p => ({ ...p, [name]: value }));
    setForm(f => ({ ...f, pricing: { ...pricing, [name]: value } }));
  };

  // --- Availability Calendar & Vacation Mode ---
  const [vacationMode, setVacationMode] = useState(false);
  const [vacationRange, setVacationRange] = useState([null, null]);
  const [availabilityDays, setAvailabilityDays] = useState(form.availabilityDays || []);
  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const toggleDay = day => {
    setAvailabilityDays(days => days.includes(day) ? days.filter(d => d !== day) : [...days, day]);
    setForm(f => ({ ...f, availabilityDays: availabilityDays.includes(day) ? availabilityDays.filter(d => d !== day) : [...availabilityDays, day] }));
  };

  // --- Notification & Locale Settings ---
  const [notificationPrefs, setNotificationPrefs] = useState(form.notificationPrefs || { email: true, sms: false, inApp: true });
  const [locale, setLocale] = useState(form.locale || 'en-US');
  const handleNotifChange = e => {
    const { name, checked } = e.target;
    setNotificationPrefs(p => ({ ...p, [name]: checked }));
    setForm(f => ({ ...f, notificationPrefs: { ...notificationPrefs, [name]: checked } }));
  };
  const handleLocaleChange = e => {
    setLocale(e.target.value);
    setForm(f => ({ ...f, locale: e.target.value }));
  };

  // --- Profile Analytics (dummy data for now) ---
  const [profileViews, setProfileViews] = useState(123);
  const [quoteRequests, setQuoteRequests] = useState(12);

  // --- Help Tooltips ---
  const help = {
    name: 'Your company or personal name as shown to customers.',
    email: 'Contact email for notifications and customer communication.',
    phone: 'Public phone number for customers.',
    locations: 'Areas you serve. Separate with commas.',
    services: 'Services you offer. Separate with commas.',
    serviceDescriptions: 'Describe each service for customers.',
    availability: 'Select days you are available for moves.',
    vacation: 'Set a vacation period to block bookings.',
    notifications: 'Choose how you want to be notified.',
    locale: 'Set your preferred language and time zone.',
    brandColor: 'Pick a color for your portal theme.'
  };

  // --- Unified Analytics Section with Interactive Charts ---
  // --- Chart Data Preparation ---
  // Defensive: always clone arrays to avoid mutating frozen objects (for Rollup/MUI X Charts)
  // Guard against undefined or non-array allBookings
  const safeAllBookings = Array.isArray(allBookings) ? allBookings : [];
  const bookingStatusData = ['pending','confirmed','completed','cancelled'].map((status, idx) => ({
    id: status,
    value: Math.max(0, safeAllBookings.filter(b => b && b.status === status).length),
    label: `${status.charAt(0).toUpperCase() + status.slice(1)}`,
    color: ['#2563eb','#22c55e','#f59e42','#e11d48'][idx],
  }));

  const months = Array.from({length: 6}, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - (5-i));
    return `${d.getFullYear()}-${d.getMonth()+1}`;
  });
  // Always clone months array for chart xAxis
  const monthsCloned = [...months];
  const monthlyBookings = months.map(m => {
    const [year, month] = m.split('-');
    const bookings = safeAllBookings.filter(b => {
      if (!b || !b.moveDate) return false;
      const d = new Date(b.moveDate);
      return !isNaN(d.getTime()) && d.getFullYear() === Number(year) && d.getMonth()+1 === Number(month);
    });
    return Math.max(0, bookings.length);
  });
  const monthlyRevenue = months.map(m => {
    const [year, month] = m.split('-');
    const bookings = safeAllBookings.filter(b => {
      if (!b || !b.moveDate) return false;
      const d = new Date(b.moveDate);
      return !isNaN(d.getTime()) && d.getFullYear() === Number(year) && d.getMonth()+1 === Number(month);
    });
    return Math.max(0, bookings.reduce((sum, b) => sum + (Number(b.price) || 0), 0));
  });

  const completedBookings = safeAllBookings.filter(b => b.status === 'completed');
  const totalRevenue = completedBookings.reduce((sum, booking) => sum + (Number(booking.price) || 0), 0);
  const averageJobValue = completedBookings.length > 0 ? totalRevenue / completedBookings.length : 0;
  const performanceMetrics = performance?.metrics;
  const completionRate = performanceMetrics?.completionRate ?? 0;
  const averageRating = performanceMetrics?.averageRating ?? null;

  const itemCounts = {};
  safeAllBookings.forEach(b => {
    if (b && Array.isArray(b.inventory)) {
      b.inventory.forEach(id => {
        if (id) {
          itemCounts[id] = (itemCounts[id] || 0) + 1;
        }
      });
    }
  });
  const popularItems = Object.entries(itemCounts).sort((a,b) => b[1]-a[1]).slice(0,5);
  // Always clone arrays for chart props
  const popularItemIds = [...popularItems.map(([id]) => id)];
  const popularItemCounts = [...popularItems.map(([_, count]) => Math.max(0, Number(count) || 0))];

  // --- Booking Status Action Handler ---
  const [actionLoading, setActionLoading] = useState(false);
  const handleBookingStatusAction = async (bookingId, status) => {
    setActionLoading(true);
    try {
      const res = await apiFetch(`/api/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      setSnackbar({ open: true, message: `Booking marked as ${status}.`, severity: 'success' });
      // Update status and filter out completed/cancelled from recentBookings
      setRecentBookings(bks => bks
        .map(b => b._id === bookingId ? { ...b, status } : b)
        .filter(b => b.status === 'pending' || b.status === 'confirmed')
      );
      setUpcomingMoves(mvs => mvs.map(b => b._id === bookingId ? { ...b, status } : b));
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to update status.', severity: 'error' });
      console.error('Error updating booking status:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const renderOverviewSection = () => (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderColor: '#E0ECEE', borderRadius: 1.25, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
              <Box>
                <Typography sx={{ color: '#173057', fontWeight: 800, fontSize: '1rem' }}>Recent activity</Typography>
                <Typography sx={{ color: '#7790A5', fontSize: '.7rem', mt: .25 }}>Your latest confirmed and pending moves</Typography>
              </Box>
              <Button href="/mover-portal/bookings" size="small" sx={{ fontSize: '.68rem', color: '#008D7B' }}>View all</Button>
            </Box>
            {recentBookings.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">No recent bookings</Typography>
                <Typography variant="body2" color="text.secondary">New bookings will appear here</Typography>
              </Box>
            ) : (
              <Box sx={{ maxHeight: isMobile ? 'none' : 276, overflow: 'auto' }}>
                {recentBookings.slice(0, isMobile ? 3 : 4).map(booking => (
                  <Box key={booking._id} sx={{ p: 1.25, mb: .75, border: '1px solid #E8F0F2', borderRadius: 1, bgcolor: '#FFFFFF' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ color: '#173057', fontSize: '.75rem', fontWeight: 800 }} noWrap={isMobile}>
                          {booking.contactName || booking.user?.name || 'Customer'}
                        </Typography>
                        <Typography sx={{ color: '#60748D', fontSize: '.65rem', mt: .25 }}>#{booking.jobNumber || booking._id?.slice(-6)} · {booking.moveDate ? new Date(booking.moveDate).toLocaleDateString() : 'Date TBD'}</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                        <Chip label={booking.status} size="small" sx={{ height: 18, fontSize: '.55rem', textTransform: 'capitalize', bgcolor: booking.status === 'confirmed' ? '#E6F7EE' : '#FFF5DC', color: booking.status === 'confirmed' ? '#16804B' : '#996A00', fontWeight: 800 }} />
                        {booking.price && <Typography sx={{ color: '#173057', fontSize: '.68rem', fontWeight: 800, mt: .5 }}>${booking.price.toLocaleString()}</Typography>}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderColor: '#E0ECEE', borderRadius: 1.25, height: '100%' }}>
            <Typography sx={{ color: '#173057', fontWeight: 800, fontSize: '1rem', mb: .25 }}>Profile completion</Typography>
            <Typography sx={{ color: '#7790A5', fontSize: '.7rem', mb: 2 }}>Keep your company details up to date.</Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ color: '#60748D', fontSize: '.7rem' }}>Company profile</Typography>
                <Typography sx={{ color: '#008D7B', fontSize: '.7rem', fontWeight: 800 }}>{profileCompletion}%</Typography>
              </Box>
              <LinearProgress variant="determinate" value={profileCompletion} sx={{ height: 6, borderRadius: 4, bgcolor: '#E7F0F1', '& .MuiLinearProgress-bar': { bgcolor: '#008D7B' } }} />
            </Box>
            <Divider sx={{ borderColor: '#E8F0F2', my: 1.75 }} />
            <Box sx={{ display: 'grid', gap: 1.1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ color: '#60748D', fontSize: '.7rem' }}>Completion rate</Typography><Typography sx={{ color: '#173057', fontSize: '.7rem', fontWeight: 800 }}>{completionRate}%</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ color: '#60748D', fontSize: '.7rem' }}>Upcoming moves</Typography><Typography sx={{ color: '#173057', fontSize: '.7rem', fontWeight: 800 }}>{upcomingMoves.length}</Typography></Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography sx={{ color: '#60748D', fontSize: '.7rem' }}>Customer rating</Typography><Typography sx={{ color: '#173057', fontSize: '.7rem', fontWeight: 800 }}>{averageRating !== null ? `${averageRating.toFixed(1)} / 5` : 'No reviews'}</Typography></Box>
            </Box>
            <Button href="/mover-portal/profile" variant="contained" fullWidth size="small" sx={{ mt: 2.25, bgcolor: '#008D7B', fontSize: '.68rem', '&:hover': { bgcolor: '#007566' } }}>Complete profile</Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderAnalyticsSection = () => (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3, display: isMobile ? 'none' : 'block' }}>Analytics & Trends</Typography>
      <Grid container spacing={isMobile ? 2 : 4}>
        <Grid item xs={12} md={6}>
          <Paper elevation={4} sx={{ p: isMobile ? 2 : 3, borderRadius: 3, boxShadow: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PieChartIcon color="primary" sx={{ mr: 1, fontSize: 32 }} />
              <Typography variant="h6" fontWeight={700}>Booking Status Breakdown</Typography>
            </Box>
            {safeAllBookings.length > 0 ? <PieChart series={[{ data: [...bookingStatusData] }]} width={isMobile ? 280 : 340} height={220} slotProps={{ legend: { hidden: false } }} /> : <Typography variant="body2">No bookings data.</Typography>}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={4} sx={{ p: isMobile ? 2 : 3, borderRadius: 3, boxShadow: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LineStyleIcon color="secondary" sx={{ mr: 1, fontSize: 32 }} />
              <Typography variant="h6" fontWeight={700}>Monthly Bookings & Revenue</Typography>
            </Box>
            <LineChart xAxis={[{ data: monthsCloned, label: 'Month' }]} series={[{ data: [...monthlyBookings], label: 'Bookings', color: '#2563eb' }, { data: [...monthlyRevenue], label: 'Revenue', color: '#22c55e' }]} width={isMobile ? 280 : 340} height={220} slotProps={{ legend: { hidden: false } }} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={4} sx={{ p: isMobile ? 2 : 3, borderRadius: 3, boxShadow: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BarChartIcon color="success" sx={{ mr: 1, fontSize: 32 }} />
              <Typography variant="h6" fontWeight={700}>Popular Inventory Items</Typography>
            </Box>
            <BarChart xAxis={[{ data: [...popularItemIds], label: 'Item' }]} series={[{ data: [...popularItemCounts], label: 'Count', color: '#2563eb' }]} width={isMobile ? 280 : 340} height={220} slotProps={{ legend: { hidden: false } }} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={4} sx={{ p: isMobile ? 2 : 3, borderRadius: 3, boxShadow: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StarIcon color="warning" sx={{ mr: 1, fontSize: 32 }} />
              <Typography variant="h6" fontWeight={700}>Recent Reviews</Typography>
            </Box>
            {reviews.length > 0 ? (
              <Box>
                <Typography variant="body1" fontWeight={600} sx={{ mb: 1 }}>Average Rating: {(reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length).toFixed(2)} / 5</Typography>
                {reviews.map(r => (
                  <Paper key={r._id} sx={{ p: 2, mb: 1, borderRadius: 2, boxShadow: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>{r.customerName?.[0] || '?'}</Avatar>
                      <Typography variant="subtitle2" fontWeight={700}>{r.customerName || 'Customer'}</Typography>
                      <Chip icon={<StarIcon />} label={r.rating} color="warning" sx={{ ml: 2, fontWeight: 700 }} />
                    </Box>
                    <Typography variant="body2">{r.comment}</Typography>
                  </Paper>
                ))}
              </Box>
            ) : <Typography variant="body2">No reviews yet.</Typography>}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderBookingsSection = () => (
    <Box>
      <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 1 }}>
          <Typography variant="h6" fontWeight={700}>Recent Bookings</Typography>
          <Button variant="outlined" size="small" onClick={handleExportBookings} disabled={downloading}>{downloading ? 'Exporting...' : 'Export'}</Button>
        </Box>
        {recentBookings.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">No recent bookings</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>New bookings will appear here</Typography>
          </Box>
        ) : (
          <Grid container spacing={isMobile ? 2 : 3}>
            {recentBookings.map(booking => (
              <Grid item xs={12} md={6} lg={4} key={booking._id}>
                <Paper elevation={2} sx={{ p: isMobile ? 2 : 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>{booking.contactName || booking.user?.name || 'Customer'}</Typography>
                      <Typography variant="caption" color="text.secondary">Job #{booking.jobNumber || booking._id?.slice(-6)}</Typography>
                    </Box>
                    <Chip label={booking.status} color={booking.status === 'confirmed' ? 'success' : booking.status === 'pending' ? 'warning' : booking.status === 'completed' ? 'primary' : 'default'} size="small" sx={{ fontWeight: 600 }} />
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>📅 {booking.moveDate ? new Date(booking.moveDate).toLocaleDateString() : 'Date TBD'}</Typography>
                    {booking.origin && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>📍 {booking.origin} → {booking.destination}</Typography>}
                    {booking.price && <Typography variant="body1" fontWeight={600} color="primary.main">${booking.price.toLocaleString()}</Typography>}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {booking.status !== 'confirmed' && <Button size="small" variant="contained" color="primary" disabled={actionLoading} onClick={() => handleBookingStatusAction(booking._id, 'confirmed')} sx={{ fontSize: '0.75rem' }}>Confirm</Button>}
                    {booking.status !== 'completed' && <Button size="small" variant="contained" color="success" disabled={actionLoading} onClick={() => handleBookingStatusAction(booking._id, 'completed')} sx={{ fontSize: '0.75rem' }}>Complete</Button>}
                    <Button size="small" variant="outlined" sx={{ fontSize: '0.75rem' }}>View Details</Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );

  const renderReportsSection = () => <CustomReportingTools />;

  // Show skeleton loader while loading
  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <EnhancedErrorBoundary>
      <Box sx={{ width: '100%', height: '100%', p: { xs: 1, sm: 2, md: 3 }, bgcolor: 'background.default', overflow: 'auto', pb: { xs: 10, md: 3 } }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto', width: '100%' }}>
          {profile?.services?.includes('storage') && (
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Storage Rentals Analytics</Typography>
              <Suspense fallback={<CircularProgress size={24} />}>
                <AnalyticsDashboard token={token} type="storage" />
              </Suspense>
            </Paper>
          )}

          <Snackbar open={snackbar.open} autoHideDuration={2000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
            <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
          </Snackbar>

          <Box sx={{ mb: 2.25 }}>
            <Typography sx={{ color: '#173057', fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.45rem' } }}>Good morning{profile?.name ? `, ${profile.name.split(' ')[0]}` : ''}.</Typography>
            <Typography sx={{ color: '#60748D', fontSize: '.75rem', mt: .4 }}>Here is what is happening with your business today.</Typography>
          </Box>

          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            {[{ label: 'New bookings', value: recentBookings.length, note: 'Pending or confirmed' }, { label: 'Upcoming moves', value: upcomingMoves.length, note: 'Scheduled jobs' }, { label: 'Completed revenue', value: `$${totalRevenue.toLocaleString()}`, note: 'Completed bookings' }, { label: 'Customer rating', value: averageRating !== null ? averageRating.toFixed(1) : '—', note: averageRating !== null ? 'Out of 5' : 'No reviews yet' }].map(metric => (
              <Grid item xs={6} md={3} key={metric.label}>
                <Paper variant="outlined" sx={{ p: { xs: 1.35, md: 1.75 }, borderColor: '#E0ECEE', borderRadius: 1.25 }}>
                  <Typography sx={{ color: '#60748D', fontSize: '.65rem', fontWeight: 700 }}>{metric.label}</Typography>
                  <Typography sx={{ color: '#173057', fontSize: { xs: '1.2rem', md: '1.45rem' }, fontWeight: 800, mt: .6 }}>{metric.value}</Typography>
                  <Typography sx={{ color: '#7790A5', fontSize: '.58rem', mt: .3 }}>{metric.note}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {renderOverviewSection()}
        </Box>
      </Box>

      {isMobile && <MobileBottomNavigation dashboardTab={dashboardTab} setDashboardTab={setDashboardTab} notifications={notifications.length} />}
    </EnhancedErrorBoundary>
  );
};

export default MoverDashboard;
